import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowRight, ShieldCheck } from 'lucide-react';
import { DrawnFace, PromoImg } from './PromoImg';
import { SamplePhotosTag, SectionHeading } from './SectionHeading';
import { APPROVED_DRAWING, APPROVED_DRAWING_FACES, DRAWN_FACES, DRAWN_PEOPLE, SAMPLE_NAMES, SAMPLE_PHOTOS } from './promo';

/** Where each name tag sits on its face ring: under Mia's (on her raincoat), right of Ana's (on the sky). */
const TAG_SIDE = {
  mia: 'left-1/2 top-full mt-1 -translate-x-1/2',
  ana: 'left-full top-1/2 ml-1 -translate-y-1/2',
} as const;

/**
 * "From your photo to their story": Mia's and Ana's sample photos above the
 * faces the storybook drew from them, then the cover those faces are cut
 * from, with a ring on each face so it is plain where each close-up comes
 * from. Every drawing here is the one the owner approved (promo.ts); Tom is
 * not in it, so he is not here either.
 */
export function PhotoToStorySection({ onPrivacyDetails }: { onPrivacyDetails: () => void }) {
  const { t } = useTranslation();
  return (
    <section id="examples" className="scroll-mt-20 py-14 sm:py-20" aria-labelledby="storybook-examples-title">
      <div className="container mx-auto px-4">
        <SectionHeading
          id="storybook-examples-title"
          eyebrow={t('storybookPage.examples.eyebrow')}
          title={t('storybookPage.examples.title')}
          subtitle={t('storybookPage.examples.subtitle')}
        />

        <div className="mx-auto grid max-w-5xl items-center gap-8 lg:grid-cols-[minmax(0,0.85fr)_auto_minmax(0,1.15fr)] lg:gap-6">
          <figure className="glass-card mx-auto w-full max-w-md p-4 sm:p-5 lg:max-w-none">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <SamplePhotosTag label={t('storybookPage.promo.samplePhotos')} />
              <span className="text-xs font-semibold text-muted-foreground">{t('storybookPage.examples.drawnLabel')}</span>
            </div>
            <ul className="grid grid-cols-2 gap-4 sm:gap-5">
              {DRAWN_PEOPLE.map((person) => (
                <li key={person} className="flex min-w-0 flex-col items-center text-center">
                  <PromoImg
                    image={SAMPLE_PHOTOS[person]}
                    sizes="(min-width: 1024px) 180px, (min-width: 480px) 190px, 42vw"
                    className="aspect-square w-full rounded-2xl object-cover shadow-sm"
                  />
                  <ArrowDown className="my-2 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  <DrawnFace
                    image={DRAWN_FACES[person].image}
                    face={DRAWN_FACES[person].face}
                    sizes="(min-width: 480px) 720px, 160vw"
                    alt={t('storybookPage.promo.drawnAlt', { name: SAMPLE_NAMES[person] })}
                    className="w-full rounded-2xl bg-[#cde8f2] shadow-sm ring-2 ring-white"
                  />
                  <p className="mt-2 text-sm font-semibold leading-tight">{t(`storybookPage.promo.people.${person}`)}</p>
                </li>
              ))}
            </ul>
            <figcaption className="mt-4 text-xs leading-relaxed text-muted-foreground">
              {t('storybookPage.examples.pairsCaption')}
            </figcaption>
          </figure>

          <span aria-hidden="true" className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
            <ArrowDown className="h-5 w-5 lg:hidden" />
            <ArrowRight className="hidden h-5 w-5 lg:block" />
          </span>

          <figure className="relative mx-auto w-full max-w-md lg:max-w-none">
            {/* The page block behind the cover. */}
            <div aria-hidden="true" className="absolute inset-0 translate-x-2 translate-y-2 rounded-3xl bg-[#f3ead9] shadow-[0_10px_24px_rgba(32,41,74,0.18)]" />
            <div className="relative overflow-hidden rounded-3xl bg-[#cde8f2] shadow-[0_18px_40px_rgba(32,41,74,0.22)] ring-1 ring-black/5">
              <PromoImg
                image={APPROVED_DRAWING}
                sizes="(min-width: 1024px) 470px, (min-width: 480px) 448px, calc(100vw - 2rem)"
                className="aspect-square w-full object-cover"
              />
              {DRAWN_PEOPLE.map((person) => {
                const face = APPROVED_DRAWING_FACES[person];
                return (
                  <span
                    key={person}
                    aria-hidden="true"
                    className="absolute h-[17%] w-[17%] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow-[0_0_0_1px_rgba(32,41,74,0.25),0_6px_16px_rgba(32,41,74,0.25)]"
                    style={{ left: `${face.x * 100}%`, top: `${face.y * 100}%` }}
                  >
                    <span
                      className={`absolute whitespace-nowrap rounded-full bg-white px-2 py-0.5 text-[11px] font-bold leading-tight text-[#20294a] shadow ${TAG_SIDE[person]}`}
                    >
                      {SAMPLE_NAMES[person]}
                    </span>
                  </span>
                );
              })}
            </div>
            {/* Top right: open sky in the approved cover (the kite fills the top left). */}
            <figcaption className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[#20294a] shadow-sm">
              {t('storybookPage.examples.coverLabel')}
            </figcaption>
          </figure>
        </div>

        <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed text-muted-foreground">
          {t('storybookPage.examples.note')}
        </p>
        <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-muted-foreground">
          <ShieldCheck className="mr-1.5 inline h-4 w-4 align-[-3px] text-[hsl(160_60%_28%)]" aria-hidden="true" />
          {t('storybookPage.examples.privacy')}{' '}
          <button
            type="button"
            onClick={onPrivacyDetails}
            // A 44px tap target that keeps the line's height: the padding
            // grows the hit area, the negative margin gives the space back.
            className="-my-3 whitespace-nowrap py-3 font-semibold text-primary underline underline-offset-2 hover:no-underline"
          >
            {t('storybookPage.examples.privacyLink')}
          </button>
        </p>
      </div>
    </section>
  );
}
